import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, ChevronRight, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "bc_ai_banner_dismissed";

/**
 * Banner neon vibrante in HomePage che annuncia la disponibilità della
 * schedina "Pronostici AI del Giorno" pubblicata dall'admin.
 *
 * - Visibile a TUTTI gli utenti (no gating). L'utente clicca → va a "I Miei".
 * - Dismissable per pubblicazione: una volta chiuso non torna fino alla
 *   prossima pubblicazione (chiave dismiss include il published_at timestamp).
 */
export default function AiPicksBanner() {
  const navigate = useNavigate();
  const [active, setActive] = useState(null); // {date, published_at, n_picks, summary}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/ai-picks/active`);
        const items = res.data?.items || [];
        if (!items.length) return;
        const latest = items[0]; // sorted desc by date backend-side
        const dismissedKey = `${DISMISS_KEY}_${latest.published_at || latest.date}`;
        if (localStorage.getItem(dismissedKey) === "1") return;
        if (!cancelled) setActive(latest);
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!active) return null;

  // Neon palette — cyan-violet gradient that vibrates
  const neon1 = "#06FFE4";   // electric cyan
  const neon2 = "#FF00E0";   // hot magenta
  const neon3 = "#A78BFA";   // violet accent

  const handleDismiss = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(`${DISMISS_KEY}_${active.published_at || active.date}`, "1");
    } catch { /* ignore */ }
    setActive(null);
  };

  const handleClick = () => {
    navigate("/miei-pronostici");
  };

  return (
    <>
      <style>{`
        @keyframes bcNeonPulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px ${neon1}aa,
              0 0 14px ${neon1}66,
              0 0 28px ${neon1}33,
              inset 0 0 18px ${neon1}22;
          }
          50% {
            box-shadow:
              0 0 0 1px ${neon2}aa,
              0 0 20px ${neon2}88,
              0 0 40px ${neon2}55,
              inset 0 0 24px ${neon2}33;
          }
        }
        @keyframes bcNeonSweep {
          0%   { background-position:   0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position:   0% 50%; }
        }
        @keyframes bcNeonGlitch {
          0%, 100% { transform: translate(0, 0); opacity: 1; }
          92%      { transform: translate(0, 0); opacity: 1; }
          93%      { transform: translate(-1px, 0); opacity: 0.85; }
          95%      { transform: translate(1px, 0); opacity: 1; }
          97%      { transform: translate(0, 0); opacity: 0.92; }
        }
        @keyframes bcNeonChevron {
          0%, 100% { transform: translateX(0); }
          50%      { transform: translateX(4px); }
        }
      `}</style>

      <div
        data-testid="ai-picks-banner"
        onClick={handleClick}
        role="button"
        tabIndex={0}
        style={{
          position: "relative",
          cursor: "pointer",
          margin: "0.85rem 0 1rem 0",
          padding: "0.85rem 1.1rem 0.85rem 1.25rem",
          // Animated gradient background — sweeps cyan ↔ magenta ↔ violet
          background: `linear-gradient(90deg,
            ${neon1}1c 0%,
            ${neon3}22 35%,
            ${neon2}1c 70%,
            ${neon1}1c 100%)`,
          backgroundSize: "200% 100%",
          border: "1px solid transparent",
          // Pulsing neon glow
          animation: "bcNeonPulse 2.4s ease-in-out infinite, bcNeonSweep 6s linear infinite",
          display: "flex",
          alignItems: "center",
          gap: "0.9rem",
          overflow: "hidden",
        }}
      >
        {/* Scanline / glitch overlay */}
        <div
          aria-hidden
          style={{
            position: "absolute", inset: 0, pointerEvents: "none",
            background: `repeating-linear-gradient(
              180deg,
              transparent 0px,
              transparent 3px,
              rgba(255,255,255,0.025) 3px,
              rgba(255,255,255,0.025) 4px)`,
            mixBlendMode: "overlay",
          }}
        />

        {/* Icon — sparkles, pulsing */}
        <Zap
          size={20}
          style={{
            color: neon1,
            filter: `drop-shadow(0 0 8px ${neon1}) drop-shadow(0 0 14px ${neon2}88)`,
            flexShrink: 0,
            animation: "bcNeonGlitch 3s ease-in-out infinite",
          }}
        />

        <div style={{ flex: 1, minWidth: 0, position: "relative", zIndex: 1 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap",
          }}>
            <span
              data-testid="ai-picks-banner-title"
              style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontWeight: 900,
                fontSize: "1rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#FFFFFF",
                // Neon text glow
                textShadow: `
                  0 0 4px ${neon1},
                  0 0 10px ${neon1}cc,
                  0 0 18px ${neon2}88`,
              }}
            >
              Pronostici del giorno disponibili in <span style={{ color: neon1 }}>&quot;I Miei&quot;</span>
            </span>
            <Sparkles size={13} style={{ color: neon2, filter: `drop-shadow(0 0 6px ${neon2})` }} />
            <span style={{
              padding: "0.12rem 0.5rem",
              background: "rgba(0,0,0,0.45)",
              border: `1px solid ${neon1}88`,
              color: neon1,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.6rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              textShadow: `0 0 6px ${neon1}aa`,
            }}>
              {active.n_picks} picks
            </span>
          </div>
          <div style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "0.76rem",
            color: "rgba(255,255,255,0.78)",
            lineHeight: 1.4,
            marginTop: "0.15rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {active.summary || "Apri ‘I Miei’ per vedere la schedina AI del giorno."}
          </div>
        </div>

        <ChevronRight
          size={18}
          style={{
            color: neon1,
            filter: `drop-shadow(0 0 6px ${neon1})`,
            flexShrink: 0,
            animation: "bcNeonChevron 1.4s ease-in-out infinite",
          }}
        />
        <button
          onClick={handleDismiss}
          data-testid="ai-picks-banner-dismiss"
          aria-label="Chiudi banner"
          style={{
            background: "rgba(0,0,0,0.35)",
            border: `1px solid rgba(255,255,255,0.15)`,
            color: "rgba(255,255,255,0.7)",
            cursor: "pointer",
            padding: "0.25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#fff";
            e.currentTarget.style.borderColor = neon2;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "rgba(255,255,255,0.7)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
          }}
        >
          <X size={14} />
        </button>
      </div>
    </>
  );
}
